import { useCallback, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import type { ColumnDef } from "@tanstack/react-table";
import { Table } from "@common/Table";
import { Title, SubTitle, ErrorState, Button } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useBillingApi } from "@/hooks/apiHooks/useBillingApi";
import { useMountRead } from "@/hooks/useMountRead";
import {
    unconfirmedChargesAtom,
    isFetchingUnconfirmedChargesAtom,
    isClearingUnconfirmedChargeAtom,
} from "@/store/billingAtoms";
import { useNotification } from "@components/Notification/hooks/useNotification";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { useSessionSetAtom } from "@/hooks/useSessionSetAtom";
import { feeChargeBlocksAtom } from "@/store/orderAtoms";
import { normalizeGuid } from "@utils/sameGuid";
import {
    type ClearChargeRequest,
    type UnconfirmedActivationCharge,
    type UnconfirmedCharges as UnconfirmedChargesList,
    type UnconfirmedRenewalCharge,
    type UnconfirmedReturnedPaymentFee,
} from "@models/billing";
import {
    SUBSCRIPTION_STATUS_LABEL,
    parseSubscriptionStatus,
} from "@models/organization";
import { asApiFailure } from "@utils/apiFailure";
import { currencyFormatter, formatDateTime } from "@utils/formatter";
import { ClearChargeDialog } from "./_components/ClearChargeDialog";
import {
    clearChargeErrorMessage,
    clearFeeChargeErrorMessage,
} from "./_helpers/clearChargeError";
import {
    activationClearSuccess,
    renewalClearSuccess,
    returnedPaymentFeeClearSuccess,
    UNDESCRIBED_CLEAR,
    type ClearChargeSuccess,
} from "./_helpers/clearChargeSuccess";
import {
    MEMBERSHIP_RESOLUTION_DETAILS,
    RETURNED_PAYMENT_FEE_RESOLUTION_DETAILS,
} from "./_helpers/resolutionDetails";

// Which row the open dialog is clearing. Each kind POSTs to its own endpoint
// (location, organization/subscription, or order).
type ClearTarget =
    | { kind: "activation"; row: UnconfirmedActivationCharge }
    | { kind: "renewal"; row: UnconfirmedRenewalCharge }
    | { kind: "returnedPaymentFee"; row: UnconfirmedReturnedPaymentFee };

// The vendor transaction id sitting in Authorize.Net's review queue — the
// operator's reconciliation key. null = plain unconfirmed charge (nothing to
// release at the vendor).
const heldTransactionCell = (heldTransactionId: string | null) =>
    heldTransactionId ? (
        <span className="font-mono">{heldTransactionId}</span>
    ) : (
        "—"
    );

// The fields every row kind shares — the shape the shared column defs read.
interface UnconfirmedChargeRowBase {
    organizationName: string;
    unconfirmedSince: string;
    heldTransactionId: string | null;
}

// The open row as the list last loaded it: a refusal reloads the list, and the
// dialog must follow a charge that turned held (or stopped being held). A row
// that left the list keeps its last-known state.
const latestRowOf = (
    target: ClearTarget,
    charges: UnconfirmedChargesList | null,
): UnconfirmedChargeRowBase => {
    switch (target.kind) {
        case "activation":
            return (
                charges?.locations.find(
                    (row) => row.locationId === target.row.locationId,
                ) ?? target.row
            );
        case "renewal":
            return (
                charges?.subscriptions.find(
                    (row) => row.organizationId === target.row.organizationId,
                ) ?? target.row
            );
        case "returnedPaymentFee":
            return (
                charges?.returnedPaymentFees.find(
                    (row) => row.orderId === target.row.orderId,
                ) ?? target.row
            );
    }
};

const dialogSubjectOf = (target: ClearTarget): string => {
    switch (target.kind) {
        case "activation":
            return `${target.row.locationName} — ${target.row.organizationName}`;
        case "renewal":
            return target.row.organizationName;
        case "returnedPaymentFee":
            return `Order ${target.row.orderNumberFormatted} — ${target.row.organizationName}`;
    }
};

interface SharedWorklistColumns<T> {
    organizationName: ColumnDef<T>;
    unconfirmedSince: ColumnDef<T>;
    heldTransactionId: ColumnDef<T>;
    actions: ColumnDef<T>;
}

// Column defs shared verbatim by every worklist: the tables differ only in
// their kind-specific middle columns and in the ClearTarget the action opens.
// A function declaration, not an arrow: ESLint's indent rule mis-computes the
// body offset for generic arrows with `extends` in .tsx files.
function sharedWorklistColumns<T extends UnconfirmedChargeRowBase>(
    toTarget: (row: T) => ClearTarget,
    openDialog: (target: ClearTarget) => void,
): SharedWorklistColumns<T> {
    return {
        organizationName: {
            id: "organizationName",
            accessorFn: (row) => row.organizationName,
            header: "Organization",
            enableSorting: false,
        },
        unconfirmedSince: {
            id: "unconfirmedSince",
            accessorFn: (row) => row.unconfirmedSince,
            header: "Unconfirmed Since",
            enableSorting: false,
            cell: ({ getValue }) => formatDateTime(getValue() as string),
        },
        heldTransactionId: {
            id: "heldTransactionId",
            accessorFn: (row) => row.heldTransactionId,
            header: "Held Transaction",
            enableSorting: false,
            cell: ({ row }) =>
                heldTransactionCell(row.original.heldTransactionId),
        },
        actions: {
            id: "actions",
            header: "",
            enableSorting: false,
            cell: ({ row }) => (
                <Button
                    color={ComponentColor.Primary}
                    size={ComponentSize.SM}
                    onClick={() => openDialog(toTarget(row.original))}
                >
                    Clear…
                </Button>
            ),
        },
    };
}

// Admin worklist for charge-outcome blocks (#377) with the void-vs-release
// resolution flow (#382). Rows are locations blocked on activation,
// memberships blocked on renewal, and orders whose returned-payment fee charge
// is unconfirmed (#469); each stays blocked until an admin reconciles the
// charge against Authorize.Net and clears it here.
export const UnconfirmedCharges = () => {
    const {
        getUnconfirmedCharges,
        clearRenewalCharge,
        clearActivationCharge,
        clearReturnedPaymentFeeCharge,
    } = useBillingApi();

    const charges = useAtomValue(unconfirmedChargesAtom);
    const isFetching = useAtomValue(isFetchingUnconfirmedChargesAtom);
    const isClearing = useAtomValue(isClearingUnconfirmedChargeAtom);

    const { showSuccess, showInfo } = useNotification();

    const [target, setTarget] = useState<ClearTarget | null>(null);
    const [clearError, setClearError] = useState<string | null>(null);
    // One clear at a time: two presses in one tick would otherwise send two
    // requests before the processing atom disables Confirm.
    const runGuarded = useSubmitGuard();

    // Issued on mount. `reloadCharges` resolves whether the list reloaded and
    // never rejects. The page shows a failure only for its own read (#407).
    const { readStatus, reload: reloadCharges } = useMountRead(
        getUnconfirmedCharges,
    );

    const openDialog = useCallback((nextTarget: ClearTarget) => {
        setClearError(null);
        setTarget(nextTarget);
    }, []);

    const closeDialog = () => {
        setClearError(null);
        setTarget(null);
    };

    // #469 round 1b: a fee clear the API accepted (a no-op included: the
    // charge is no longer unconfirmed) resolves the charge the order page's
    // session block was waiting on, so the fee can be charged again there.
    // Never a block that outlives a clear (charge_not_recorded: that charge
    // moved money the order may not record, and this clear is not about it).
    const setFeeChargeBlocks = useSessionSetAtom(feeChargeBlocksAtom);
    const liftFeeChargeBlock = (orderId: string) => {
        const key = normalizeGuid(orderId);
        setFeeChargeBlocks((blocks) => {
            if (!blocks[key] || blocks[key].outlivesClear) {
                return blocks;
            }
            const remaining = { ...blocks };
            delete remaining[key];
            return remaining;
        });
    };

    const handleConfirm = (request: ClearChargeRequest) =>
        runGuarded(async () => {
            if (!target) {
                return;
            }
            setClearError(null);

            // Only the request sits in the failure path; the success copy is
            // built after it, so describing a committed clear can never
            // report it as failed.
            let describeOutcome: () => ClearChargeSuccess;
            try {
                if (target.kind === "activation") {
                    const response = await clearActivationCharge(
                        target.row.locationId,
                        request,
                    );
                    describeOutcome = () =>
                        activationClearSuccess(
                            target.row.locationName,
                            request.resolution,
                            response,
                        );
                } else if (target.kind === "renewal") {
                    const response = await clearRenewalCharge(
                        target.row.organizationId,
                        request,
                    );
                    describeOutcome = () =>
                        renewalClearSuccess(
                            target.row.organizationName,
                            request.resolution,
                            response,
                        );
                } else {
                    const response = await clearReturnedPaymentFeeCharge(
                        target.row.orderId,
                        request,
                    );
                    describeOutcome = () =>
                        returnedPaymentFeeClearSuccess(
                            target.row.orderNumberFormatted,
                            request.resolution,
                            response,
                        );
                }
            } catch (error) {
                // Keep the dialog open: the refusals carry operator
                // instructions rendered inline. Every refusal reloads the
                // list first, so the open dialog follows the row as it now is
                // (a charge that turned held offers Released), and the
                // conflict copy says whether the reload worked.
                const status = asApiFailure(error)?.status;
                const listReloaded =
                    (status === 400 || status === 409) &&
                    (await reloadCharges());
                const errorMessage =
                    target.kind === "returnedPaymentFee"
                        ? clearFeeChargeErrorMessage
                        : clearChargeErrorMessage;
                setClearError(errorMessage(error, listReloaded));
                return;
            }

            setTarget(null);
            if (target.kind === "returnedPaymentFee") {
                liftFeeChargeBlock(target.row.orderId);
            }
            void reloadCharges();
            let outcome: ClearChargeSuccess;
            try {
                outcome = describeOutcome();
            } catch {
                outcome = UNDESCRIBED_CLEAR;
            }
            if (outcome.changed) {
                showSuccess({ message: outcome.message });
            } else {
                showInfo({ message: outcome.message });
            }
        });

    const activationColumns = useMemo<
        ColumnDef<UnconfirmedActivationCharge>[]
    >(() => {
        const shared = sharedWorklistColumns<UnconfirmedActivationCharge>(
            (row) => ({ kind: "activation", row }),
            openDialog,
        );

        return [
            shared.organizationName,
            {
                accessorKey: "locationName",
                header: "Location",
                enableSorting: false,
            },
            shared.unconfirmedSince,
            shared.heldTransactionId,
            shared.actions,
        ];
    }, [openDialog]);

    const renewalColumns = useMemo<
        ColumnDef<UnconfirmedRenewalCharge>[]
    >(() => {
        const shared = sharedWorklistColumns<UnconfirmedRenewalCharge>(
            (row) => ({ kind: "renewal", row }),
            openDialog,
        );

        return [
            shared.organizationName,
            {
                accessorKey: "planName",
                header: "Plan",
                enableSorting: false,
            },
            {
                accessorKey: "status",
                header: "Status",
                enableSorting: false,
                // parseSubscriptionStatus normalizes an unknown wire value to
                // None → "—", so a status this build doesn't know yet renders
                // the placeholder instead of a blank cell.
                cell: ({ row }) =>
                    SUBSCRIPTION_STATUS_LABEL[
                        parseSubscriptionStatus(row.original.status)
                    ],
            },
            {
                // Clearing does NOT reset the retry ladder: a membership at the
                // attempt ceiling (or inside the cooldown) also needs a
                // reactivate before the charge job picks it up again.
                accessorKey: "renewalAttemptCount",
                header: "Attempts",
                enableSorting: false,
            },
            {
                accessorKey: "lastChargeFailureAt",
                header: "Last Failure",
                enableSorting: false,
                cell: ({ getValue }) =>
                    formatDateTime(getValue() as string | null),
            },
            shared.unconfirmedSince,
            shared.heldTransactionId,
            shared.actions,
        ];
    }, [openDialog]);

    const returnedPaymentFeeColumns = useMemo<
        ColumnDef<UnconfirmedReturnedPaymentFee>[]
    >(() => {
        const shared = sharedWorklistColumns<UnconfirmedReturnedPaymentFee>(
            (row) => ({ kind: "returnedPaymentFee", row }),
            openDialog,
        );

        return [
            shared.organizationName,
            {
                accessorKey: "locationName",
                header: "Location",
                enableSorting: false,
            },
            {
                accessorKey: "orderNumberFormatted",
                header: "Order",
                enableSorting: false,
            },
            {
                // What the unconfirmed charge tried to collect: the amount to
                // look for at Authorize.Net.
                accessorKey: "attemptedAmount",
                header: "Amount",
                enableSorting: false,
                cell: ({ row }) =>
                    row.original.attemptedAmount === null
                        ? "—"
                        : currencyFormatter.format(row.original.attemptedAmount),
            },
            {
                accessorKey: "attemptedByUserName",
                header: "Attempted By",
                enableSorting: false,
                cell: ({ row }) => row.original.attemptedByUserName ?? "—",
            },
            shared.unconfirmedSince,
            shared.heldTransactionId,
            shared.actions,
        ];
    }, [openDialog]);

    const latestRow = target ? latestRowOf(target, charges) : undefined;

    return (
        <div className="px-10">
            <Title>Unconfirmed Charges</Title>
            <p className="mt-2 text-sm text-neutral">
                Charges whose outcome is unknown or held for review at the
                vendor. Billing for these customers, or the returned-payment
                fee on these orders, is blocked until the charge is reconciled
                against Authorize.Net and cleared here.
            </p>

            {readStatus === "failed" ? (
                <ErrorState onRetry={reloadCharges} />
            ) : (
                <>
                    <div className="mt-6">
                        <SubTitle>Blocked activations (locations)</SubTitle>
                        <Table
                            data={charges?.locations ?? []}
                            columns={activationColumns}
                            size={ComponentSize.XS}
                            // Loading until this visit's read answers: the
                            // first frame must read as loading, not "No data",
                            // and not as the rows (and Clear buttons) a
                            // previous visit loaded (PR 369 review 6).
                            isLoading={isFetching || readStatus === "loading"}
                        />
                    </div>

                    <div className="mt-8">
                        <SubTitle>Blocked renewals (memberships)</SubTitle>
                        <Table
                            data={charges?.subscriptions ?? []}
                            columns={renewalColumns}
                            size={ComponentSize.XS}
                            isLoading={isFetching || readStatus === "loading"}
                        />
                    </div>

                    <div className="mt-8">
                        <SubTitle>
                            Unconfirmed returned-payment fees (orders)
                        </SubTitle>
                        <Table
                            data={charges?.returnedPaymentFees ?? []}
                            columns={returnedPaymentFeeColumns}
                            size={ComponentSize.XS}
                            isLoading={isFetching || readStatus === "loading"}
                        />
                    </div>
                </>
            )}

            {target && latestRow && (
                <ClearChargeDialog
                    subjectName={dialogSubjectOf(target)}
                    isHeld={latestRow.heldTransactionId !== null}
                    resolutionDetails={
                        target.kind === "returnedPaymentFee"
                            ? RETURNED_PAYMENT_FEE_RESOLUTION_DETAILS
                            : MEMBERSHIP_RESOLUTION_DETAILS
                    }
                    // Locked through the reload that follows a refusal too:
                    // the refusal is shown only once that reload answers.
                    isProcessing={isClearing || isFetching}
                    errorMessage={clearError}
                    onConfirm={handleConfirm}
                    onClose={closeDialog}
                    onErrorClear={() => setClearError(null)}
                />
            )}
        </div>
    );
};
